import { useContext, createContext, useState, useEffect, useRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import { shortenText } from "@/lib/utils";
import clsx from "clsx";
import Input from "../Input";

type OptionProps = {
  checked?: boolean;
  value: string;
  children: React.ReactNode;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

export type Props = {
  options: { value: string; label: string; disabled?: boolean }[];
  value?: (string | number)[];
  placeholder?: string;
  onChange?: (value: string[]) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  enabledSearch?: boolean;
};

const Context = createContext<any>(null);

const Option = ({ checked, value, children, onChange, disabled }: OptionProps) => {
  const { selectedItems, updateSelectedItems } = useContext(Context);
  const [checkedState, setCheckedState] = useState(checked);

  useEffect(() => {
    if (checked === undefined) setCheckedState(selectedItems.includes(value));
    else setCheckedState(checked);
  }, [checked, selectedItems]);

  const handleCheckedChange = () => {
    if (!disabled) {
      setCheckedState(!checkedState);
      updateSelectedItems(value);
      if (onChange) onChange(value);
    }
  };

  return (
    <li
      className={clsx(
        "flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer px-2 py-1.5",
        {
          "pointer-events-none opacity-50": disabled,
        }
      )}
      onClick={handleCheckedChange}
      data-checked={checkedState}
    >
      {checkedState && <Check className="h-4 w-4" />} {children}
    </li>
  );
};

const Provider = ({ children }: { children: React.ReactNode }) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const updateSelectedItems = (value: string) =>
    setSelectedItems(
      selectedItems.includes(value)
        ? selectedItems.filter((item) => item !== value)
        : [...selectedItems, value]
    );

  return (
    <Context.Provider
      value={{ selectedItems, updateSelectedItems, setSelectedItems }}
    >
      {children}
    </Context.Provider>
  );
};

const MultipleSelectBody = ({
  children,
  options,
  onChange,
  placeholder,
  disabled,
  className,
  value,
  enabledSearch=false,
}: Props) => {
  const { selectedItems, setSelectedItems } = useContext(Context);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const clickedOutside = (e: MouseEvent) => {
    if (!ref.current?.contains(e.target as Node)) setOpen(false);
  };

  useEffect(() => {
    if (
      value?.length &&
      (selectedItems.length
        ? selectedItems.filter((item: string) => !value?.includes(item))?.length
        : true)
    ) {
      setSelectedItems(value);
    }

    if (value?.length === 0 && selectedItems.length > 0) setSelectedItems([]);
  }, [value]);

  useEffect(() => {
    window.addEventListener("click", clickedOutside);
    onChange?.(selectedItems);
    setSearch('');
    return () => {
      window.removeEventListener("click", clickedOutside);
    };
  }, [selectedItems]);

  useEffect(() => {
    if (open && enabledSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, enabledSearch]);

  const handleTriggerClick = () => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <div className="multiple-select relative text-sm" ref={ref}>
      <div
        className={clsx(
          "border border-input bg-background inline-flex items-center justify-between w-full h-10 px-2 rounded-sm",
          className,
          {
            "cursor-pointer": !disabled,
            "cursor-not-allowed opacity-50": disabled,
          }
        )}
        onClick={handleTriggerClick}
      >
        <div className='w-full'>
          {selectedItems.length ? (
            <span className='truncate inline-block w-full'>
              {options
                  ?.filter((option) => selectedItems.includes(option.value))
                  .map((option) => option.label)
                  .join(", ")}
            </span>
          ) : (
            <span className="">{placeholder || "Select"}</span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </div>
      {open && (
        <div className={`absolute z-50 bg-white dark:bg-background w-full rounded-sm border dark:border-white shadow-md`}>
          {enabledSearch && (
            <Input ref={searchInputRef} value={search} placeholder='Search' className='dark:placeholder-darkTextColor dark:text-darkTextColor focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0' onChange={(e) => setSearch(e.currentTarget.value.toLowerCase())}></Input>)}
          
          <ul
            className={`w-full overflow-y-auto max-h-96`}
          >
            {children
              ? children
              : options.filter((option) => option.label.toLowerCase().includes(search) || option.disabled).map((option, index) => (
                  <Option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </Option>
                ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MultipleSelect = (props: Props) => {
  return (
    <Provider>
      <MultipleSelectBody {...props} />
    </Provider>
  );
};

export default MultipleSelect;